<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useFormat } from '@/composables/useFormat'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'StockOpname',
  },
})

const route   = useRoute()
const router  = useRouter()
const ability = useAbility()
const { formatNumber } = useFormat()
const { request, loading: actionLoading } = useApiRequest()

const opnameId = computed(() => route.params.id)

// ─── State ────────────────────────────────────────────────────────────────────
const opname      = ref(null)
const pageLoading = ref(true)

const confirmDialog = ref(false)
const cancelDialog  = ref(false)

// Inline edit
const editingItemId = ref(null)
const editForm      = ref({ stock_actual: null, notes: '' })
const editErrors    = ref({})
const editLoading   = ref(false)

// ─── Fetch ────────────────────────────────────────────────────────────────────
const fetchOpname = async () => {
  pageLoading.value = true
  try {
    const response = await useApi(`/api/v1/stock-opnames/${opnameId.value}`).get()
    const result   = response?.data ?? response
    opname.value   = result?.data ?? result
  } catch (err) {
    console.error('fetchOpname error:', err)
  } finally {
    pageLoading.value = false
  }
}

onMounted(fetchOpname)

// ─── Computed ─────────────────────────────────────────────────────────────────
const isDraft = computed(() => opname.value?.status === 'draft')

// ─── Helpers ──────────────────────────────────────────────────────────────────
const statusColor = s => ({ draft: 'warning', confirmed: 'success', cancelled: 'error' }[s] ?? 'default')
const statusLabel = s => ({ draft: 'Draft', confirmed: 'Dikonfirmasi', cancelled: 'Dibatalkan' }[s] ?? s)
const diffColor   = diff => {
  if (Math.abs(Number(diff)) < 0.0001) return 'success'
  return Number(diff) > 0 ? 'info' : 'error'
}

// FIX: formatDate dan formatDateTime didefinisikan di sini
// (sebelumnya tidak ada → template crash)
const formatDate = (val) => {
  if (!val) return '-'
  return new Date(val).toLocaleDateString('id-ID', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

const formatDateTime = (val) => {
  if (!val) return '-'
  return new Date(val).toLocaleString('id-ID', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// ─── Edit Item ────────────────────────────────────────────────────────────────
const startEdit = (item) => {
  editingItemId.value = item.id
  editForm.value      = {
    stock_actual: Number(item.stock_actual),  // pastikan number, bukan string
    notes:        item.notes ?? '',
  }
  editErrors.value = {}
}

const cancelEdit = () => {
  editingItemId.value = null
  editErrors.value    = {}
}

const saveItem = async (item) => {
  editLoading.value = true
  editErrors.value  = {}

  try {
    // FIX: kirim stock_actual sebagai number eksplisit
    // Sebelumnya v-model.number kadang masih string jika langsung diset
    await useApi(`/api/v1/stock-opnames/${opnameId.value}/items/${item.id}`).patch({
      body: {
        stock_actual: Number(editForm.value.stock_actual),
        notes:        editForm.value.notes || null,
      },
    })

    // Update item in-place tanpa refetch seluruh halaman
    const idx = opname.value.items.findIndex(i => i.id === item.id)
    if (idx !== -1) {
      opname.value.items[idx] = {
        ...opname.value.items[idx],
        stock_actual: Number(editForm.value.stock_actual),
        notes:        editForm.value.notes,
        difference:   Number(editForm.value.stock_actual) - Number(opname.value.items[idx].stock_system),
      }
    }

    cancelEdit()
  } catch (err) {
    console.error('saveItem error:', err)
    const status = err?.status ?? err?.response?.status
    const body   = err?.data  ?? err?.response?._data ?? {}

    if (status === 422) {
      // Tampilkan field-level errors jika ada
      editErrors.value = body?.errors ?? {}
      // Jika tidak ada field errors, tampilkan pesan umum
      if (!Object.keys(editErrors.value).length) {
        editErrors.value = { stock_actual: [body?.message ?? 'Validasi gagal.'] }
      }
    }
  } finally {
    editLoading.value = false
  }
}

// ─── Confirm ──────────────────────────────────────────────────────────────────
const confirmOpname = async () => {
  await request(async () => {
    await useApi(`/api/v1/stock-opnames/${opnameId.value}/confirm`).post()
    confirmDialog.value = false
    await fetchOpname()
  })
}

// ─── Cancel ───────────────────────────────────────────────────────────────────
const cancelOpname = async () => {
  await request(async () => {
    await useApi(`/api/v1/stock-opnames/${opnameId.value}/cancel`).post()
    cancelDialog.value = false
    await fetchOpname()
  })
}

// ─── Table headers ────────────────────────────────────────────────────────────
const itemHeaders = [
  { title: 'Bahan Baku',   key: 'ingredient',   sortable: false },
  { title: 'Stok Sistem',  key: 'stock_system',  align: 'end', width: '140px' },
  { title: 'Stok Aktual',  key: 'stock_actual',  align: 'end', width: '200px' },
  { title: 'Selisih',      key: 'difference',    align: 'end', width: '120px' },
  { title: 'Catatan',      key: 'notes',         sortable: false },
  { title: 'Aksi',         key: 'actions',       sortable: false, align: 'center', width: '90px' },
]
</script>

<template>
  <VRow>
    <VCol cols="12">
      <div class="d-flex align-center mb-6">
        <VBtn variant="text" icon="ri-arrow-left-line" class="me-2" :to="{ name: 'inventory-stock-opnames' }" />
        <h4 class="text-h4 font-weight-bold">Detail Stock Opname</h4>
      </div>
    </VCol>

    <!-- Loading skeleton -->
    <VCol v-if="pageLoading" cols="12">
      <VCard>
        <VCardText>
          <VSkeletonLoader type="heading, table-row@6" />
        </VCardText>
      </VCard>
    </VCol>

    <template v-else-if="opname">
      <!-- Header Card -->
      <VCol cols="12">
        <VCard class="mb-4">
          <VCardText>
            <VRow align="center">
              <VCol cols="12" md="8">
                <div class="d-flex align-center gap-3 mb-2">
                  <h5 class="text-h5 font-weight-bold">{{ opname.opname_number }}</h5>
                  <VChip :color="statusColor(opname.status)" label>
                    {{ statusLabel(opname.status) }}
                  </VChip>
                </div>
                <div class="d-flex flex-wrap gap-x-5 gap-y-1 text-body-2 text-medium-emphasis">
                  <span>
                    <VIcon icon="ri-calendar-line" size="15" class="me-1" />
                    {{ formatDate(opname.opname_date) }}
                  </span>
                  <span>
                    <VIcon icon="ri-user-line" size="15" class="me-1" />
                    {{ opname.creator?.name ?? '-' }}
                  </span>
                  <span v-if="opname.confirmed_by">
                    <VIcon icon="ri-check-double-line" size="15" class="me-1" />
                    {{ opname.confirmed_by?.name }}
                    <span v-if="opname.confirmed_at" class="ms-1">
                      ({{ formatDateTime(opname.confirmed_at) }})
                    </span>
                  </span>
                </div>
                <p v-if="opname.notes" class="text-body-2 mt-2">
                  <VIcon icon="ri-sticky-note-line" size="15" class="me-1" />
                  {{ opname.notes }}
                </p>
              </VCol>

              <VCol cols="12" md="4">
                <div class="d-flex gap-5 justify-md-end mb-4">
                  <div class="text-center">
                    <p class="text-h5 font-weight-bold">{{ opname.total_items ?? 0 }}</p>
                    <p class="text-caption text-medium-emphasis">Total Item</p>
                  </div>
                  <div class="text-center">
                    <p
                      class="text-h5 font-weight-bold"
                      :class="(opname.items_with_diff ?? 0) > 0 ? 'text-error' : 'text-success'"
                    >
                      {{ opname.items_with_diff ?? 0 }}
                    </p>
                    <p class="text-caption text-medium-emphasis">Ada Selisih</p>
                  </div>
                </div>

                <div v-if="isDraft" class="d-flex gap-2 justify-md-end">
                  <VBtn
                    v-if="ability.can('update', 'StockOpname')"
                    color="success"
                    prepend-icon="ri-check-double-line"
                    @click="confirmDialog = true"
                  >
                    Konfirmasi
                  </VBtn>
                  <VBtn
                    v-if="ability.can('delete', 'StockOpname')"
                    variant="tonal"
                    color="error"
                    prepend-icon="ri-close-line"
                    @click="cancelDialog = true"
                  >
                    Batalkan
                  </VBtn>
                </div>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Hint draft -->
      <VCol v-if="isDraft" cols="12">
        <VAlert color="warning" variant="tonal" density="compact" class="mb-2">
          <VIcon icon="ri-pencil-line" class="me-1" />
          Status <strong>Draft</strong> — klik ikon edit (✏️) pada baris bahan baku,
          input stok aktual, lalu klik ✓ untuk menyimpan. Setelah semua terisi, klik
          <strong>Konfirmasi</strong>.
        </VAlert>
      </VCol>

      <!-- Items Table -->
      <VCol cols="12">
        <VCard>
          <VCardTitle class="px-6 pt-4 pb-2">Daftar Bahan Baku</VCardTitle>
          <VDataTable
            :headers="itemHeaders"
            :items="opname.items ?? []"
            :hide-default-footer="(opname.items ?? []).length <= 10"
            class="text-no-wrap"
          >
            <!-- Ingredient -->
            <template #item.ingredient="{ item }">
              <div>
                <p class="font-weight-medium mb-0">{{ item.ingredient?.name }}</p>
                <p v-if="item.ingredient?.sku" class="text-caption text-medium-emphasis mb-0">
                  {{ item.ingredient.sku }}
                </p>
              </div>
            </template>

            <!-- Stok Sistem -->
            <template #item.stock_system="{ item }">
              {{ formatNumber(item.stock_system) }} {{ item.ingredient?.unit?.symbol ?? '' }}
            </template>

            <!-- Stok Aktual — input saat editing -->
            <template #item.stock_actual="{ item }">
              <div v-if="editingItemId === item.id" style="min-width: 150px">
                <VTextField
                  v-model.number="editForm.stock_actual"
                  type="number"
                  min="0"
                  step="any"
                  density="compact"
                  variant="outlined"
                  :error-messages="editErrors?.stock_actual"
                  :suffix="item.ingredient?.unit?.symbol"
                  hide-details="auto"
                />
              </div>
              <span v-else>
                {{ formatNumber(item.stock_actual) }} {{ item.ingredient?.unit?.symbol ?? '' }}
              </span>
            </template>

            <!-- Selisih -->
            <template #item.difference="{ item }">
              <VChip
                :color="diffColor(item.difference)"
                size="small"
                variant="tonal"
              >
                {{ Number(item.difference) > 0 ? '+' : '' }}{{ formatNumber(item.difference) }}
              </VChip>
            </template>

            <!-- Catatan — input saat editing -->
            <template #item.notes="{ item }">
              <VTextField
                v-if="editingItemId === item.id"
                v-model="editForm.notes"
                density="compact"
                variant="outlined"
                placeholder="Catatan..."
                :error-messages="editErrors?.notes"
                style="min-width: 160px"
                hide-details="auto"
              />
              <span v-else class="text-body-2 text-medium-emphasis">
                {{ item.notes || '-' }}
              </span>
            </template>

            <!-- Aksi -->
            <template #item.actions="{ item }">
              <!-- Mode editing -->
              <template v-if="editingItemId === item.id">
                <VBtn
                  icon variant="text" size="small" color="success"
                  :loading="editLoading"
                  @click="saveItem(item)"
                >
                  <VIcon icon="ri-check-line" size="18" />
                  <VTooltip activator="parent">Simpan</VTooltip>
                </VBtn>
                <VBtn icon variant="text" size="small" color="secondary" @click="cancelEdit">
                  <VIcon icon="ri-close-line" size="18" />
                  <VTooltip activator="parent">Batal</VTooltip>
                </VBtn>
              </template>

              <!-- Tombol edit — hanya saat draft & punya permission -->
              <VBtn
                v-else-if="isDraft && ability.can('update', 'StockOpname')"
                icon variant="text" size="small" color="primary"
                @click="startEdit(item)"
              >
                <VIcon icon="ri-edit-line" size="18" />
                <VTooltip activator="parent">Edit Stok Aktual</VTooltip>
              </VBtn>

              <span v-else class="text-disabled">—</span>
            </template>

            <template #no-data>
              <div class="text-center py-4 text-medium-emphasis text-body-2">
                Tidak ada item
              </div>
            </template>
          </VDataTable>
        </VCard>
      </VCol>
    </template>

    <!-- Not found -->
    <VCol v-else cols="12">
      <VAlert color="warning" variant="tonal">
        Data opname tidak ditemukan.
        <VBtn variant="text" size="small" :to="{ name: 'inventory-stock-opnames' }">
          Kembali ke list
        </VBtn>
      </VAlert>
    </VCol>

    <!-- Dialog Konfirmasi -->
    <VDialog v-model="confirmDialog" max-width="500">
      <VCard>
        <VCardTitle class="pa-6 pb-2">Konfirmasi Stock Opname</VCardTitle>
        <VCardText class="px-6">
          <p class="mb-3">Apakah Anda yakin ingin mengkonfirmasi sesi opname ini?</p>
          <VAlert color="warning" variant="tonal" density="compact">
            Stok semua bahan baku yang memiliki selisih akan diperbarui otomatis
            sesuai stok aktual yang diinput.
          </VAlert>
        </VCardText>
        <VCardActions class="pa-6 pt-3">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" @click="confirmDialog = false">Batal</VBtn>
          <VBtn color="success" :loading="actionLoading" @click="confirmOpname">
            Ya, Konfirmasi
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Dialog Batalkan -->
    <VDialog v-model="cancelDialog" max-width="420">
      <VCard>
        <VCardTitle class="pa-6 pb-2">Batalkan Opname?</VCardTitle>
        <VCardText class="px-6">
          Sesi opname akan dibatalkan dan stok tidak akan berubah.
          Tindakan ini tidak bisa dibatalkan.
        </VCardText>
        <VCardActions class="pa-6 pt-3">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" @click="cancelDialog = false">Tidak</VBtn>
          <VBtn color="error" :loading="actionLoading" @click="cancelOpname">
            Ya, Batalkan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </VRow>
</template>
